// store.ts — typed front-end wrappers over the on-device Rust SQLite commands.
// Ported from the archived Flutter build's data/models.dart + Express sync surface,
// but every call now hits LOCAL SQLite via Tauri IPC (durable, offline, on-disk).
//
// Data model (matches the live server brain contract):
//   mood entry { date, score 1..5, note, tags }

import { invoke } from "./tauri-web";

export interface MoodEntry {
  id: string;
  /** Calendar date the user is logging FOR (local tz), "YYYY-MM-DD". */
  logged_on: string;
  /** "HH:MM" local. Several entries can share a day; this is what orders them. */
  logged_at: string;
  /** Mood value 1..5 (1 = rough, 5 = great). */
  score: number;
  /** Optional free-text note. "" when not provided. */
  note: string;
  /** Activity tags, normalized lowercase. */
  tags: string[];
  created_at: string;
  updated_at: string;
}

export interface NewMoodEntry {
  score: number;
  logged_on?: string;
  /** "HH:MM"; omitted = now. */
  logged_at?: string;
  note?: string;
  tags?: string[];
}

export type MoodEntryPatch = Partial<{
  score: number;
  logged_on: string;
  /** "HH:MM" local — lets an edit move an entry to a different time of day. */
  logged_at: string;
  note: string;
  tags: string[];
}>;

// ── Derived signals (computed on-device in Rust; only aggregates are sent to the brain) ──
// Mirrors src-tauri/src/signals.rs::WeekSignals. Every field is a computed aggregate —
// counts, averages, deltas, std-devs, weekly means — never a raw note. This is the
// payload the AI reflection reasons over (see brain.ts), so the model can name SPECIFIC
// non-obvious patterns instead of re-reading the diary.
export interface WindowStats {
  label: string;
  start: string;
  end: string;
  entries: number;
  days_logged: number;
  avg_score: number | null;
  min_score: number | null;
  max_score: number | null;
}
export interface WindowShift {
  current_avg: number;
  prior_avg: number;
  delta: number;
  direction: "up" | "down" | "flat";
  current_entries: number;
  prior_entries: number;
}
export interface TagCorrelation {
  tag: string;
  count: number;
  avg_with_tag: number;
  overall_avg: number;
  delta: number;
  direction: "higher" | "lower" | "same";
}
export interface DowStat { weekday: string; count: number; avg_score: number | null }
export interface Volatility {
  current_std_dev: number | null;
  prior_std_dev: number | null;
  change: "steadier" | "more variable" | "similar" | null;
}
export interface WeekPoint { iso_week: string; avg_score: number; entries: number }
export interface Trajectory {
  weeks: WeekPoint[];
  slope_per_week: number;
  direction: "rising" | "falling" | "flat";
}
export interface MoodSignals {
  schema: string;
  generated_for: string;
  overall: {
    total_entries: number;
    days_span: number;
    avg_score: number | null;
    first_date: string | null;
    last_date: string | null;
    scale: string;
  };
  current_window: WindowStats;
  prior_window: WindowStats | null;
  window_shift: WindowShift | null;
  tag_correlations: TagCorrelation[];
  day_of_week: DowStat[];
  volatility: Volatility;
  trajectory: Trajectory | null;
}

export const store = {
  listEntries: (opts?: { from?: string; to?: string; limit?: number }) =>
    invoke<MoodEntry[]>("list_entries", { from: opts?.from, to: opts?.to, limit: opts?.limit ?? 50000 }),
  getEntry: (id: string) => invoke<MoodEntry | null>("get_entry", { id }),
  getEntryForDate: (date: string) => invoke<MoodEntry | null>("get_entry_for_date", { date }),
  createEntry: (input: NewMoodEntry) => invoke<MoodEntry>("create_entry", { input }),
  updateEntry: (id: string, patch: MoodEntryPatch) => invoke<void>("update_entry", { id, patch }),
  deleteEntry: (id: string) => invoke<void>("delete_entry", { id }),
  /** Compute the derived-signals object on-device. `today` = client local YYYY-MM-DD so windows align to the user's clock. */
  computeSignals: (today: string) => invoke<MoodSignals>("compute_signals", { today }),
};

// ── Pure policy (ported verbatim-in-spirit from lib/src/policy/*.dart) ─────────

/** Default 1..5 scale labels — human, non-clinical. awful → great. */
export const MOOD_LABELS = ["Rough", "Meh", "Okay", "Good", "Great"] as const;
/** Default emoji set. Order matches scores 1..5. */
export const MOOD_EMOJI = ["😞", "😕", "😐", "🙂", "😄"] as const;
/** Mood-scale colours — muted, never traffic-light (DESIGN.md §3, light mode). */
export const MOOD_COLOR = ["#C0607A", "#C98A4B", "#B0A98F", "#6FA67A", "#4F9D8E"] as const;

export function emojiForScore(score: number): string {
  return score >= 1 && score <= 5 ? MOOD_EMOJI[score - 1] : "❓";
}
export function labelForScore(score: number): string {
  return score >= 1 && score <= 5 ? MOOD_LABELS[score - 1] : "Unknown";
}
export function colorForScore(score: number): string {
  return score >= 1 && score <= 5 ? MOOD_COLOR[score - 1] : "#8A8595";
}

/** Curated activity-tag suggestions; user can also type their own. */
export const DEFAULT_TAGS = [
  "work", "family", "friends", "exercise", "sleep", "food",
  "outdoors", "hobbies", "relaxing", "travel", "health", "creative",
];

/** Normalize a tag — lowercase, trimmed, whitespace→hyphen. */
export function normalizeTag(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, "-");
}
/** Dedupe + normalize, preserving first-occurrence order. */
export function normalizeTags(raw: Iterable<string>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const r of raw) {
    const n = normalizeTag(r);
    if (!n || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
}

const toLocalDate = (iso: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};
export const formatLocalDate = (d: Date): string =>
  `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
export const todayLocal = (): string => formatLocalDate(new Date());

/**
 * Streak = consecutive run of days (from today, walking back) with ≥1 entry,
 * with one day of grace (logging this morning doesn't break yesterday's streak).
 */
export function computeStreak(loggedOnDates: string[], today = new Date()): number {
  if (loggedOnDates.length === 0) return 0;
  const cursor = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = new Set<string>();
  for (const d of loggedOnDates) {
    const parsed = toLocalDate(d);
    if (parsed) days.add(formatLocalDate(parsed));
  }
  let cur = new Date(cursor);
  if (!days.has(formatLocalDate(cur))) {
    cur.setDate(cur.getDate() - 1);
    if (!days.has(formatLocalDate(cur))) return 0;
  }
  let streak = 0;
  while (days.has(formatLocalDate(cur))) {
    streak++;
    cur.setDate(cur.getDate() - 1);
  }
  return streak;
}

export function streakCopy(streak: number): string {
  if (streak === 0) return "Log today to start one";
  if (streak === 1) return "1-day streak — nice start!";
  if (streak < 7) return `${streak}-day streak · going great`;
  if (streak < 14) return `${streak}-day streak · keep it rolling`;
  if (streak < 30) return `${streak}-day streak · you are on a roll`;
  if (streak < 100) return `${streak}-day streak · that is commitment`;
  return `${streak}-day streak · 🏆 you are unstoppable`;
}

export interface DayBucket {
  date: string;
  averageScore: number | null;
  count: number;
}
export interface WeeklyTrend {
  days: DayBucket[]; // 7, oldest → newest
  weekAverage: number | null;
  daysLogged: number;
}

/** Aggregate (date, score) tuples into the 7-day window ending today. */
export function computeWeeklyTrend(entries: { logged_on: string; score: number }[], today = new Date()): WeeklyTrend {
  const cursorDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const weekStart = new Date(cursorDay);
  weekStart.setDate(weekStart.getDate() - 6);

  const sums = new Map<string, { sum: number; count: number }>();
  for (const e of entries) {
    const parsed = toLocalDate(e.logged_on);
    if (!parsed) continue;
    const key = formatLocalDate(parsed);
    let score = e.score;
    if (score < 1) score = 1;
    if (score > 5) score = 5;
    const cur = sums.get(key);
    sums.set(key, cur ? { sum: cur.sum + score, count: cur.count + 1 } : { sum: score, count: 1 });
  }

  const days: DayBucket[] = [];
  let total = 0;
  let daysLogged = 0;
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    const key = formatLocalDate(d);
    const acc = sums.get(key);
    if (!acc) {
      days.push({ date: key, averageScore: null, count: 0 });
    } else {
      const avg = acc.sum / acc.count;
      days.push({ date: key, averageScore: avg, count: acc.count });
      total += avg;
      daysLogged += 1;
    }
  }
  return { days, weekAverage: daysLogged > 0 ? total / daysLogged : null, daysLogged };
}

export function trendSummaryCopy(t: WeeklyTrend): string {
  if (t.daysLogged === 0) return "Log your first mood — even one entry starts the chart.";
  if (t.daysLogged === 7) return "All 7 days logged this week 🌟";
  if (t.weekAverage !== null && t.weekAverage >= 4) return `${t.daysLogged}/7 days this week · feeling good`;
  if (t.weekAverage !== null && t.weekAverage >= 3) return `${t.daysLogged}/7 days this week · holding steady`;
  return `${t.daysLogged}/7 days this week · be gentle with yourself`;
}
